import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';

import { throwResponseAsLumResponseError } from 'v2/shared/api/lum-response-error';

import { IOrderTransfer } from '../../../../order/interfaces/order-transfer.interface';
import { IPagination, IPaginationResponse } from '../../../interfaces';
import { EnvironmentUtilService } from '../../util';
import { ApiService } from '../api.service';

export enum EOrderTransferRelation {
  CREATED_BY = 'createdBy',
  UPDATED_BY = 'updatedBy',
  WAREHOUSE_SOURCE = 'sourceWarehouse',
  WAREHOUSE_DESTINATION = 'destinationWarehouse',
  ORDER_ITEMS = 'orderItems',
  SUPPLIER_PURCHASE_ORDER = 'supplierPurchaseOrder',
  SALES_ORDER = 'salesOrder',
}

@Injectable({
  providedIn: 'root',
})
export class OrderTransferApiService {
  private readonly _submitting = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _pagination = new BehaviorSubject<IPagination>(<IPagination>{});
  private readonly _orderTransfers = new BehaviorSubject<any>(<any>[]);
  private readonly _currentOrder = new BehaviorSubject<IOrderTransfer | null>(
    <IOrderTransfer | null>null,
  );

  environment = this.environmentUtilService.getEnvironment();

  constructor(
    private apiService :ApiService,
    private environmentUtilService :EnvironmentUtilService,
  ) {}

  get(params :{ [param :string] :string | string[] }) :Subscription {
    this._loading.next(true);

    return this.apiService
      .get(`${this.environment.urlV2}/orders/transfers`, { params })
      .subscribe({
        next: (response :IPaginationResponse) => {
          if (response) {
            this._pagination.next(response.meta);
            this._orderTransfers.next(response.data || []);
          }
          this._loading.next(false);
        },
        error: (error) => {
          this._loading.next(false);
        },
      });
  }

  subscribeGetById(data :{
    id :number;
    params :{ [param :string] :string | string[] };
  }) :Subscription {
    const { id, params } = data;
    this._loading.next(true);

    return this.apiService
      .get(`${this.environment.urlV2}/orders/transfers/${id}`, { params })
      .subscribe({
        next: (response) => {
          this._currentOrder.next(response);
          this._loading.next(false);
        },
        error: (error) => {
          this._loading.next(false);

          return throwError(() => new Error(error));
        },
      });
  }
  getById(
    id :number,
    params ?:{ [param :string] :string | string[] },
    options ?:{ updateState :boolean },
  ) :Observable<IOrderTransfer> {
    options = {
      updateState: false,
      ...options,
    };

    if (options.updateState) {
      this._loading.next(true);
    }

    return this.apiService
      .get(`${this.environment.urlV2}/orders/transfers/${id}`, {
        params,
      })
      .pipe(
        finalize(() => {
          if (options.updateState) {
            this._loading.next(false);
          }
        }),
        catchError(
          throwResponseAsLumResponseError(
            `Failed to load transfer order (id: ${JSON.stringify(id)})`,
          ),
        ),
        tap((response :IOrderTransfer) => {
          if (options.updateState) {
            this._currentOrder.next(response);
          }
        }),
      );
  }

  create(payload :any) :Observable<any> {
    this._submitting.next(true);

    return this.apiService
      .post({
        path: `${this.environment.urlV2}/orders/transfers`,
        body: payload,
      })
      .pipe(
        tap({
          next: (data) => {
            this._submitting.next(false);

            return data;
          },
          error: (error) => {
            this._submitting.next(false);

            return error;
          },
        }),
      );
  }

  delete(id :number) :Observable<any> {
    this._submitting.next(true);

    return this.apiService
      .delete(`${this.environment.urlV2}/orders/transfers/${id}`)
      .pipe(
        tap({
          next: () => {
            this._submitting.next(false);
          },
          error: () => {
            this._submitting.next(false);
          },
        }),
      );
  }

  update(data :{ id :number; payload :any }) :Observable<any> {
    const { id, payload } = data;
    this._submitting.next(true);

    return this.apiService
      .put(`${this.environment.urlV2}/orders/transfers/${id}`, payload)
      .pipe(
        tap((response :any) => {
          this._submitting.next(false);

          return response;
        }),
        catchError((response) => {
          this._submitting.next(false);

          return throwError(response.error);
        }),
      );
  }

  createTOFromSOLineItems(payload :{
    orderItemIds :number[];
    warehouseId :number;
  }) :Observable<any> {
    const { orderItemIds, warehouseId } = payload;

    return this.apiService.post({
      path: `${this.environment.urlV2}/orders/transfers/auto-create-from-sales-order-items`,
      body: { orderItemIds, warehouseId },
    });
  }

  // TODO: Change better names
  createTOFromAOLineItems(payload :{
    orderItemIds :number[];
    warehouseId :number;
  }) :Observable<any> {
    const { orderItemIds, warehouseId } = payload;

    return this.apiService.post({
      path: `${this.environment.urlV2}/orders/transfers/auto-create-from-assembly-order-items`,
      body: { orderItemIds, warehouseId },
    });
  }

  setCurrentOrder(order :IOrderTransfer | null) :void {
    this._currentOrder.next(order);
  }

  get orderTransfers$() :Observable<any[]> {
    return this._orderTransfers.asObservable();
  }

  get pagination$() :Observable<IPagination> {
    return this._pagination.asObservable();
  }

  get submitting$() :Observable<boolean> {
    return this._submitting.asObservable();
  }

  get currentOrder$() :Observable<IOrderTransfer | null> {
    return this._currentOrder.asObservable();
  }

  get loading$() :Observable<boolean> {
    return this._loading.asObservable();
  }
}
